import { useQuery } from 'react-query'

import queryClient from '../../queryClient'
import { request } from '../utils/api'
import { HTTPMethod, type Response } from '../utils/api.types'

/**
 * This map will be used to decorate the hook output, ultimately applying the boolean value from the
 * settings endpoint to each entry.
 *
 * When a new feature is added, simply add it to this enum, with the Developer facing key to the
 * left, and the endpoint key to the right.
 */
const features = {
  HasWebinarSpeakerQuestions: 'enable_webinar_speaker_questions_p',
  HasJoinToSpeakEnabled: 'enable_webinar_join_to_speak_p',
  HasSpeakerChatEnabled: 'enable_webinar_speaker_chat_p',
  HasWebinarScreenShareProcessorEnabled:
    'enable_webinar_screen_share_processor_p',
  HasWebinarGroupedQuestionsEnabled: 'enable_webinar_grouped_questions_p',
  HasWebinarChatLinksEnabled: 'enable_webinar_chat_links_p',
  HasWebinarTrayEnabled: 'enable_webinar_tray_p',
  HasWebinarHighlightsEnabled: 'enable_webinar_highlights_p',
  HasTransitionsEnabled: 'enable_webinar_transitions_p',
  HasNewChatEnabled: 'enable_webinar_new_chat_p',
  HasLiveTranscriptionsEnabled: 'enable_live_transcriptions_p',
  HasWebinarActionCardsEnabled: 'enable_webinar_action_cards_p',
  HasWebinarFocusModeEnabled: 'enable_webinar_focus_mode_p',
} as const

type Feature = keyof typeof features
type FeaturesResponse = Response & {
  data: {
    setting: (typeof features)[Feature]
    value: '0' | '1'
  }[]
}
export const featureQueryKey = 'features'

const featuresQuery = {
  queryKey: featureQueryKey,
  queryFn: async () => {
    const response = await request<{}, FeaturesResponse>({
      url: '/api/2/setting/list',
      method: HTTPMethod.GET,
    })

    return Object.entries(features).reduce(
      (acc, [key, val]) => {
        const setting =
          response.data.find((f) => f.setting === val)?.value ?? '0'

        return {
          ...acc,
          ...{ [key]: Boolean(parseInt(setting, 10)) },
        }
      },
      {} as Record<Feature, boolean>
    )
  },
  keepPreviousData: true,
  staleTime: 60 * 60 * 1000, // 1 hour
  cacheTime: 60 * 60 * 1000, // 1 hour
}

/** Returns information about a given Feature */
const useFeatures = () => {
  const { data } = useQuery(featuresQuery)
  return data ?? ({} as Record<Feature, boolean>)
}

const getFeatures = () => queryClient.fetchQuery(featuresQuery)

export default useFeatures

export { getFeatures }
